/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef } from "react";
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from "swiper/react";
// eslint-disable-next-line import/no-unresolved
import "swiper/css";

export default function SwiperTabs({
  swiperRef,
  onIndexChange,
  initialIndex = 0,
  autoHeightWatch = true,
  swiperOptions = {},
  autoHeightDeps = [],
  updateAutoHeightDuration = 200,
  children,
}) {
  const defaultProps = {
    autoHeight: true,
    speed: 350,
    resistanceRatio: 0.85,
    slidesPerView: 1,
    simulateTouch: false,
    allowTouchMove: true,
    noSwiping: true,
    noSwipingClass: "swiper-no-swiping",
    observer: true,
    observeParents: true,
    updateOnWindowResize: true,
    touchStartPreventDefault: false,
  };

  const resizeObsRef = useRef(null);
  const observedElRef = useRef(null);
  const isMountedRef = useRef(true);

  const attachResizeObserver = (swiper) => {
    if (!autoHeightWatch || !swiper || !swiper.params || swiper.destroyed)
      return;
    const el = swiper.slides?.[swiper.activeIndex];
    if (!el || observedElRef.current === el) return;
    resizeObsRef.current?.disconnect();
    observedElRef.current = el;
    resizeObsRef.current = new ResizeObserver(() => {
      if (
        isMountedRef.current &&
        swiper?.updateAutoHeight &&
        swiper.params &&
        !swiper.destroyed
      ) {
        try {
          swiper.updateAutoHeight?.(updateAutoHeightDuration);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Swiper updateAutoHeight failed:", error);
        }
      }
    });
    resizeObsRef.current.observe(el);
  };

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      resizeObsRef.current?.disconnect();
    };
  }, []);

  // Imperative autoHeight recompute when external deps change
  useEffect(() => {
    if (!autoHeightWatch) return;
    if (
      swiperRef.current &&
      swiperRef.current.params &&
      !swiperRef.current.destroyed
    ) {
      try {
        swiperRef.current.updateAutoHeight(updateAutoHeightDuration);
        attachResizeObserver(swiperRef.current);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Swiper updateAutoHeight failed:", error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoHeightWatch, updateAutoHeightDuration, ...autoHeightDeps]);

  return (
    <Swiper
      {...defaultProps}
      {...swiperOptions}
      onInit={(s) => {
        // eslint-disable-next-line no-param-reassign
        swiperRef.current = s;
        if (initialIndex) s.slideTo(initialIndex, 0);
      }}
      onActiveIndexChange={(s) => {
        onIndexChange?.(s.activeIndex);
        attachResizeObserver(s);
      }}
    >
      {React.Children.map(children, (child, i) => (
        <SwiperSlide key={child.key ?? i}>{child}</SwiperSlide>
      ))}
    </Swiper>
  );
}
